import { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Box, Paper, Typography, IconButton } from '@mui/material';
import 'dayjs/locale/pt-br';

interface CalendarProps {
    selectedDate: Dayjs;
    onDateSelect: (date: Dayjs) => void;
    datesWithAppointments: Set<string | null>;
}

export default function Calendar({ selectedDate, onDateSelect, datesWithAppointments }: CalendarProps) {
    const [currentMonth, setCurrentMonth] = useState(selectedDate);

    const startOfMonth = currentMonth.startOf('month');
    const endOfMonth = currentMonth.endOf('month');
    const startDate = startOfMonth.startOf('week');
    const endDate = endOfMonth.endOf('week');

    const handlePreviousMonth = () => {
        setCurrentMonth(currentMonth.subtract(1, 'month'));
    };

    const handleNextMonth = () => {
        setCurrentMonth(currentMonth.add(1, 'month'));
    };

    const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    const days = [];
    let day = startDate;

    while (day.isBefore(endDate) || day.isSame(endDate, 'day')) {
        days.push(day);
        day = day.add(1, 'day');
    }

    return (
        <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <IconButton onClick={handlePreviousMonth} size="small">
                    <ChevronLeft size={20} />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {currentMonth.locale("pt-br").format('MMMM YYYY')}
                </Typography>
                <IconButton onClick={handleNextMonth} size="small">
                    <ChevronRight size={20} />
                </IconButton>
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: 1,
                }}
            >
                {weekDays.map((weekDay) => (
                    <Box
                        key={weekDay}
                        sx={{
                            textAlign: 'center',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            color: 'text.secondary',
                            py: 1,
                        }}
                    >
                        {weekDay}
                    </Box>
                ))}

                {days.map((day, index) => {
                    const isCurrentMonth = day.month() === currentMonth.month();
                    const isSelected = day.isSame(selectedDate, 'day');
                    const isToday = day.isSame(dayjs(), 'day');
                    const hasAppointments = datesWithAppointments.has(day.format('YYYY-MM-DD'));

                    return (
                        <Box
                            key={index}
                            onClick={() => onDateSelect(day)}
                            sx={{
                                aspectRatio: '1',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                cursor: 'pointer',
                                borderRadius: 1,
                                backgroundColor: isSelected
                                    ? 'primary.main'
                                    : isToday
                                        ? 'primary.light'
                                        : 'transparent',
                                color: isSelected
                                    ? 'primary.contrastText'
                                    : !isCurrentMonth
                                        ? 'text.disabled'
                                        : isToday
                                            ? 'primary.dark'
                                            : 'text.primary',
                                fontWeight: isSelected || isToday ? 600 : 400,
                                '&:hover': {
                                    backgroundColor: isSelected
                                        ? 'primary.dark'
                                        : 'action.hover',
                                },
                                transition: 'all 0.2s',
                            }}
                        >
                            {day.date()}
                            {hasAppointments && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 4,
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        backgroundColor: isSelected ? 'white' : 'primary.main',
                                    }}
                                />
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
}
